import { ICoreContext } from '@msdyn365-commerce/core';
import React from 'react';
import { toast } from 'sonner';
import { ILxsBillingDocument } from '../actions/get-billing-documents.action';
import GetDocumentDownload, { GetDocumentDownloadInput } from '../actions/order-details/get-document-download.action';
import { IOrderDetailDocument } from '../actions/order-details/get-order-details.action';
import GetDataSheetDocumentDownload, { GetDataSheetDocumentDownloadInput } from '../actions/order-details/get-tds-document-download.action';
import { ILxsBillingDocumentsResources } from '../modules/lxs-billing-documents/lxs-billing-documents.props.autogenerated';
import { IOrderDetailsResources } from '../modules/order-details';
import { IGetTDSDocumentItemExtended } from '../modules/order-details/components/documents-table';

type DocumentItem = IOrderDetailDocument | ILxsBillingDocument | IGetTDSDocumentItemExtended;
type DocumentResources = IOrderDetailsResources | ILxsBillingDocumentsResources;

interface IDocumentDownloadItemProps {
    context: ICoreContext;
    documentItem: DocumentItem;
    resources: DocumentResources;
    isDataSheet?: boolean;
}

const getDocumentId = (item: DocumentItem): string =>
    'Document' in item && item.Document?.DocumentId ? item.Document.DocumentId : item.DocumentId;

const getDocumentLabel = (item: DocumentItem): string => ('Name' in item ? item.Name : item.DocumentId);

const showErrorToast = (heading: string, description: string): void => {
    toast.error(
        <div className='toast' role='alert'>
            <div className='toast-header'>
                <h4>{heading}</h4>
                <button className='toast-close' onClick={() => toast.dismiss()} />
            </div>
            <div className='toast-body'>
                <span>{description}</span>
            </div>
        </div>
    );
};

const triggerBrowserDownload = (base64Content: string, fileName: string): void => {
    const tempLink = document.createElement('a');
    tempLink.href = `data:application/pdf;base64,${base64Content}`;
    tempLink.download = fileName;
    tempLink.style.display = 'none';
    document.body.appendChild(tempLink);
    tempLink.click();
    document.body.removeChild(tempLink);
};

export default function DocumentDownloadItem({ context, resources, documentItem, isDataSheet }: IDocumentDownloadItemProps) {
    const [isDownloading, setIsDownloading] = React.useState(false);

    const handleDownload = async () => {
        if (isDownloading) {
            return;
        }

        setIsDownloading(true);

        try {
            const documentId = getDocumentId(documentItem);
            let documentDownload;
            if (isDataSheet) {
                const actionInput = new GetDataSheetDocumentDownloadInput(documentItem as IGetTDSDocumentItemExtended);
                documentDownload = await GetDataSheetDocumentDownload(actionInput, context.actionContext);
            } else {
                const actionInput = new GetDocumentDownloadInput(documentId);
                documentDownload = await GetDocumentDownload(actionInput, context.actionContext);
            }
            if (!documentDownload.Content || documentDownload.Content.length === 0) {
                showErrorToast(resources.globalDownloadErrorHeading, resources.globalDownloadErrorNoContentDescription);
            } else {
                triggerBrowserDownload(documentDownload.Content, documentDownload.FileName || 'download.pdf');
            }
        } catch (error) {
            showErrorToast(resources.globalErrorHeading, resources.globalErrorServiceNotAvailable);
            console.error('Error document download', error);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <a className={`lxs-download-link ${isDownloading ? 'is-downloading' : ''}`} onClick={handleDownload} aria-busy={isDownloading}>
            <span>{getDocumentLabel(documentItem)}</span>
        </a>
    );
}
